import { useMutation } from '@tanstack/react-query';
import {
  SignInResponse,
  SsoStartRequest,
  SsoStartResponse,
  ssoStartContract,
  SsoCallbackRequest,
  ssoCallbackContract,
  SsoSignInResponse,
  SwitchTenantRequest,
  switchTenantContract,
  SsoStepUpRequest,
  SsoStepUpResponse,
  ssoStepUpContract
} from '@bliks-io/contracts';
import { apiClient } from '../client';
import type { ExtendedAxiosRequestConfig } from '@/lib/agent.types';
import { useAuthStore, type AuthSessionData } from '@/stores/auth-store';
import i18next from 'i18next';

/**
 * Adopt a freshly authenticated session, shared by the SSO callback and
 * workspace switch: store token/tenant, then switch to the user's language so
 * it is active before navigation. Exported for the $slug guard's validated
 * switch, which runs in beforeLoad (non-hook context).
 */
export function adoptSignedInSession(data: AuthSessionData): void {
  useAuthStore.getState().setAuth(data);

  if (data.user.language && data.user.language !== i18next.language) {
    i18next.changeLanguage(data.user.language);
  }
}

/**
 * SSO Start mutation
 *
 * Resolves how the entered email signs in:
 * - `{ mode: 'sso', authorizationUrl }` — redirect the browser to Zitadel
 * - `{ mode: 'unknown' }` — the email resolved to no workspace
 *
 * @example
 * ```tsx
 * function LoginForm() {
 *   const ssoStart = useSsoStart();
 *
 *   const handleContinue = async (email: string) => {
 *     const result = await ssoStart.mutateAsync({ email });
 *     if (result.mode === 'sso') {
 *       redirectBrowser(result.authorizationUrl);
 *     } else {
 *       showUnknownEmailNotice();
 *     }
 *   };
 * }
 * ```
 */
export function useSsoStart() {
  return useMutation({
    mutationFn: async (request: SsoStartRequest): Promise<SsoStartResponse> => {
      const { data } = await apiClient.post<SsoStartResponse>(ssoStartContract.path, request);
      return data;
    }
  });
}

/**
 * SSO callback exchange
 *
 * Exchanges the authorization code + state returned by Zitadel for a bliks
 * session: stores token/tenant via the auth store and switches to the user's
 * language.
 *
 * Deliberately a plain function, not a react-query mutation: codes and states
 * are single-use on the backend, so the exchange must fire exactly once and
 * outlive any component instance — observer-bound mutation state dies on
 * remount (the callback page caches this promise per state instead).
 */
export async function exchangeSsoCode(request: SsoCallbackRequest): Promise<SsoSignInResponse> {
  const { data } = await apiClient.post<SsoSignInResponse>(ssoCallbackContract.path, request);
  adoptSignedInSession(data);
  return data;
}

/**
 * SSO Step-Up mutation
 *
 * Starts a forced re-authentication (max_age=0) for the CURRENT session and
 * returns the Zitadel authorization URL. No onSuccess side effects — the
 * caller redirects the browser; the ordinary SSO callback re-mints the
 * session with a fresh auth_time and echoes `returnTo`.
 */
export function useSsoStepUp() {
  return useMutation({
    mutationFn: async (request: SsoStepUpRequest): Promise<SsoStepUpResponse> => {
      const { data } = await apiClient.post<SsoStepUpResponse>(ssoStepUpContract.path, request);
      return data;
    }
  });
}

/**
 * Raw tenant-switch POST, shared by useSwitchTenant and the $slug guard's
 * validated switch (which runs in beforeLoad, a non-hook context). The
 * guard's attempt is speculative — a failure falls back to a plain redirect —
 * so it opts out of the global 403 toast; the manual picker path relies on
 * that toast and keeps the default.
 */
export async function switchTenantRequest(
  tenantId: string,
  options: { suppressForbiddenToast?: boolean } = {}
): Promise<SignInResponse> {
  const config: Partial<ExtendedAxiosRequestConfig> | undefined = options.suppressForbiddenToast
    ? { suppressForbiddenToast: true }
    : undefined;
  const { data } = await apiClient.post<SignInResponse>(
    switchTenantContract.path,
    { tenantId },
    config
  );
  return data;
}

/**
 * Switch Tenant mutation
 *
 * Re-scopes the current identity-carrying session to another workspace the
 * caller is a member of — a fresh token minted off the membership registry,
 * no IdP round-trip. Switching into a pending membership accepts the invite.
 *
 * Adopts the new session like the SSO callback does (token, tenant,
 * language). Callers own the cache reset and navigation; use
 * `useSwitchWorkspace` instead of this raw mutation.
 */
export function useSwitchTenant() {
  return useMutation({
    mutationFn: (request: SwitchTenantRequest): Promise<SignInResponse> =>
      switchTenantRequest(request.tenantId),
    onSuccess: (data) => {
      adoptSignedInSession(data);
    }
  });
}
